import { ReactNode, useState } from 'react'
import cn from 'classnames'
import { type CertificateProgramEngagementCta } from '@mc/api'
import { IconCheckBoldDefault } from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import { useIsEnterpriseMember } from 'pages/certificates/common'
import { SubscriptionSignUpCTA } from 'pages/certificates/components'
import { PurchaseCertificateCTA } from 'pages/certificates/components/EngagementActionBlock'
import { useSignup } from 'pages/certificates/components/SignUpButton'
import { useFireEvent } from 'pages/certificates/tracking'
import { LearnMoreLink } from './LearnMoreLink'
import { SubscriptionMemberNote } from './SubscriptionMemberNote'
import { MCCERTS_2548_SUBSCRIPTIONS_ENTRY_POINT_I18N_NAMESPACE } from './i18n'
import { type CmHeroPrice } from './utils/getCmHeroPrices'

// The CM-hero subscription selector: a two-option radio-card group — the selected
// option is expanded (benefits + CTA), the other collapsed (label + price). V1
// pre-selects the subscription; V2 pre-selects the individual course.
//
// Prices come from the FCC offer endpoint (see getCmHeroPrices) — when a coupon
// applies, the discounted price is featured and the list price is struck
// through. They are undefined until the offer resolves, in which case the price
// line is hidden rather than invented.
//
// When the BE provides an engagement note (engagementNote — its details.text +
// START_TRIAL "Start Course" link, see HeroVariants), it renders inside the
// individual option, per the design.
type SelectorOption = 'subscription' | 'individual'

type SubscriptionSelectorProps = {
  defaultSelection?: SelectorOption
  individualPrice?: CmHeroPrice
  subscriptionPrice?: CmHeroPrice
  engagementNote?: { text: string; link: CertificateProgramEngagementCta }
  certUuid: string
}

const BulletItem = ({ children }: { children: ReactNode }) => (
  <li className='flex items-center gap-3'>
    <span
      aria-hidden
      className='flex items-center justify-center flex-shrink-0 w-[20px]'
    >
      <span className='w-[7px] h-[7px] rounded-sm bg-c-text-tint-3' />
    </span>
    <span className='c-body-small text-c-text-tint-3'>{children}</span>
  </li>
)

const Checkbox = ({ checked }: { checked: boolean }) => (
  <span
    aria-hidden
    className={cn(
      'flex items-center justify-center flex-shrink-0 w-[20px] h-[20px] rounded peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2',
      checked
        ? 'bg-c-brand-highlight text-c-text-tint-2'
        : 'border border-c-text-dark',
    )}
  >
    {checked && <IconCheckBoldDefault size='sm' />}
  </span>
)

const OptionCard = ({
  option,
  label,
  pricing,
  isSelected,
  onSelect,
  children,
}: {
  option: SelectorOption
  label: string
  pricing?: CmHeroPrice
  isSelected: boolean
  onSelect: (option: SelectorOption) => void
  children: ReactNode
}) => (
  <div
    className={cn('flex flex-col gap-4 p-5 relative', {
      'bg-c-base-100': isSelected,
      'bg-c-base-500': !isSelected,
      'pb-7': option === 'individual',
      'md:pb-5': option === 'individual' && !isSelected,
      'rounded-t-2xl -mb-[16px] z-0': option === 'individual',
      'rounded-2xl z-[1]': option === 'subscription',
    })}
  >
    <label className='flex items-start gap-3 cursor-pointer'>
      <input
        type='radio'
        name='subscription-selector'
        value={option}
        checked={isSelected}
        onChange={() => onSelect(option)}
        className='mc-sr-only peer'
      />
      <Checkbox checked={isSelected} />
      <div className='flex flex-col gap-1'>
        <span className='c-caption-mono text-c-text-tint-3'>{label}</span>
        {pricing?.finalPrice && (
          <span className='flex items-baseline gap-2'>
            {pricing.originalPrice && (
              <s className='c-body-small text-c-text-tint-2'>
                {pricing.originalPrice}
              </s>
            )}
            <span className='c-body-bold'>{pricing.finalPrice}</span>
          </span>
        )}
      </div>
    </label>
    {isSelected && children}
  </div>
)

export const SubscriptionSelector = ({
  defaultSelection = 'subscription',
  individualPrice,
  subscriptionPrice,
  engagementNote,
  certUuid,
}: SubscriptionSelectorProps) => {
  const { t } = useTranslation(
    MCCERTS_2548_SUBSCRIPTIONS_ENTRY_POINT_I18N_NAMESPACE,
  )
  const isEnterprise = useIsEnterpriseMember()
  const { fullHref, trackQuizExposure } = useSignup({})
  const fireEvent = useFireEvent()
  const [selected, setSelected] = useState<SelectorOption>(defaultSelection)

  // location + default_view come from the wrapping TrackingRow (hero-plan-selector).
  const handleSelect = (option: SelectorOption) => {
    setSelected(option)
    fireEvent('certificateLandingClick', { action: option })
  }

  // Purchase surface — backstop in case a caller ever bypasses
  // useIsSubscriptionsExpEligible's enterprise exclusion
  if (isEnterprise) {
    return null
  }

  return (
    <div
      role='radiogroup'
      aria-label={t('selector.groupLabel')}
      className='flex flex-col w-full'
    >
      <OptionCard
        option='individual'
        label={t('selector.individualOption')}
        pricing={individualPrice}
        isSelected={selected === 'individual'}
        onSelect={handleSelect}
      >
        <ul className='flex flex-col gap-2 list-none m-0 p-0'>
          <BulletItem>{t('selector.individual.earnCertificate')}</BulletItem>
          <BulletItem>{t('selector.individual.accessProgram')}</BulletItem>
        </ul>
        <PurchaseCertificateCTA
          text={t('selector.individual.cta')}
          href={fullHref}
          onClick={trackQuizExposure}
          className='w-full flex items-center justify-center'
        />
        {engagementNote && (
          <SubscriptionMemberNote
            text={engagementNote.text}
            link={engagementNote.link}
            certUuid={certUuid}
          />
        )}
      </OptionCard>

      <OptionCard
        option='subscription'
        label={t('selector.subscriptionOption')}
        pricing={subscriptionPrice}
        isSelected={selected === 'subscription'}
        onSelect={handleSelect}
      >
        <ul className='flex flex-col gap-2 list-none m-0 p-0'>
          <BulletItem>{t('selector.subscription.flatPrice')}</BulletItem>
          <BulletItem>{t('selector.subscription.proofPerSkill')}</BulletItem>
          <BulletItem>
            <LearnMoreLink>
              {t('selector.subscription.learnMore')}
            </LearnMoreLink>
          </BulletItem>
        </ul>
        <SubscriptionSignUpCTA />
      </OptionCard>
    </div>
  )
}
